/**
 * SelfRegistrationHero — left-panel content for the public resident signup page.
 *
 * Renders a property's brand (logo + name) at the top when branding is available,
 * falling back to the generic Secure Labs welcome messaging otherwise. A two-step
 * stepper below the heading communicates "submit → await approval" so residents
 * understand the flow before submitting.
 */

interface SelfRegistrationHeroProps {
  propertyName?: string;
  logoUrl?: string | null;
  primaryColour?: string | null;
}

const STEPS = [{ label: 'Submit Details' }, { label: 'Await Approval' }] as const;

export function SelfRegistrationHero({
  propertyName,
  logoUrl,
  primaryColour,
}: SelfRegistrationHeroProps) {
  const accent = primaryColour ?? 'var(--estate-accent)';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${propertyName ?? 'Property'} logo`}
            className="size-10 rounded-md object-contain"
          />
        ) : null}
        <div>
          <p className="text-lg font-semibold text-[var(--text-primary)]">
            {propertyName ? `Join ${propertyName}` : 'Welcome to Secure Labs'}
          </p>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Submit your details — your property manager will review and approve.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-0">
        {STEPS.map((step, i) => (
          <div key={step.label} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className="flex size-7 items-center justify-center rounded-full text-xs font-semibold"
                style={
                  i === 0
                    ? { backgroundColor: accent, color: 'var(--text-inverse)' }
                    : {
                        backgroundColor: 'var(--bg-surface-raised)',
                        color: 'var(--text-tertiary)',
                      }
                }
              >
                {i + 1}
              </div>
              <span
                className="whitespace-nowrap text-[11px]"
                style={{ color: i === 0 ? 'var(--text-accent)' : 'var(--text-tertiary)' }}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className="mx-2 mb-5 h-px w-16"
                style={{ backgroundColor: 'var(--border-default)' }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
